<template>
  <!-- A focused card fills its 2x2 grid area so the gallery has leftover height to absorb. -->
  <div
    class="group relative f-col"
    :class="{ 'h-full': isFocused }"
    data-test-id="product-card"
  >
    <base-button
      v-if="!isComingSoon && !isRobisOnly"
      :aria-describedby="product.id"
      :aria-label="$t.add"
      class="absolute right-0 top-0 z-3 p-1 group-@hover:op-100 duration lg:op-0 group-focus-within:lg:op-100"
      data-test-id="product-card-add"
      style="margin-right: 0.125rem"
      @click="openQuickshop"
    >
      <span class="m-1 block rounded-xs bg-white bg-op-80 duration">
        <vf-icon name="quickshop" size="md" />
      </span>
    </base-button>
    <div v-if="galleryPending" class="aspect-square w-full skeleton" />
    <product-card-gallery
      v-else
      :asset-override
      :fetchpriority
      :focused
      :items="activeGallery"
      :lazy
      :name="data.name"
      :show-secondary-media="$feature.enableProductCardGalleryCarousel"
    >
      <template #badge>
        <product-badge
          v-if="hasBadge"
          v-bind="data.badge"
          class="absolute rounded-xs bg-white bg-op-50 px-1 py-.5 shadow-sm backdrop-blur-xs"
          data-test-id="product-card-badge"
          style="top: 0.5rem; left: 0.75rem"
        />
      </template>
      <template #link>
        <base-link aria-hidden="true" class="absolute-0" tabindex="-1" :to="url" @click.capture="navigateToPDP" />
      </template>
    </product-card-gallery>
    <div
      class="relative f-col pb-1 pl-3 pr-2 pt-3 lg:p-2"
      style="gap: 0.125rem"
    >
      <div
        v-if="showEyebrow"
        class="line-clamp-1 eyebrow c-brand-1"
        data-test-id="product-card-badge"
        style="height: 1rem"
      >
        {{ data.eyebrow?.label }}
      </div>
      <base-link
        :id="product.id"
        class="line-clamp-2 self-start subtitle-6"
        data-test-id="product-card-title"
        :to="url"
        @click.capture="navigateToPDP"
      >
        {{ data.name }}
        <span class="absolute-0" />
      </base-link>
      <div v-if="$feature.showShortDescriptionInProductList && data.shortDescription" class="body-4 c-grey-30">
        {{ data.shortDescription }}
      </div>
      <span
        v-if="!hideRating && data?.rating?.reviewsCount"
        :aria-label="replaceAll($t.ratingLabel, { value: data?.rating?.score?.toFixed(1), max: 5 })"
        class="mb-2 w-max items-center gap-0.5 c-brand-1 i-flex"
      >
        <span class="text-xs">{{ data?.rating?.score?.toFixed(1) }}</span>
        <vf-icon aria-hidden="true" name="star-filled" />
      </span>
      <span v-if="colorsCount" class="flex body-5 c-brand-1">
        {{ colorsCount > 15 ? '15+' : colorsCount }} {{ $pluralize($t.colors, colorsCount).toLowerCase() }}
      </span>
      <product-pricing
        class="body-5"
        :currency="data.currency"
        data-test-id="product-card-pricing"
        :price="productPrice"
        :show-discount-percentage
      />
      <div v-if="$feature.showBuyWithPrimeOnPLP && product?.primeIntended" class="mt-2 flex items-center body-5">
        <base-picture
          alt="Prime"
          class="mr-2"
          height="24"
          src="https://assets.timberland.eu/image/upload/v1771246760/prime.svg"
          width="45"
        />{{ $t.optionsAvailable }}
      </div>
    </div>

    <panel-quickshop v-slot="props" class-wrapper="!p-0" :size="{ sm: 'lg', lg: 'full' }" :title="$t.size">
      <dialog-quickshop v-bind="props" :product-id="swatchId" />
    </panel-quickshop>
  </div>
</template>

<script lang="ts" setup>
import type { Emits, Props } from '#commerce/components/product/card/Card.vue'
import type { Product } from '#root/api/clients/product/data-contracts'
import type { ProductClickAction } from '#types/gtm'
import type { ColorOption } from '#types/product'
import { GalleryContextKey } from '#commerce/contexts/gallery'

const {
  breadcrumbs = [],
  focused,
  imageFetchPriority: fetchpriority,
  trackingOffset,
  product,
  storeFilterState,
  trackingCategory,
  trackingList,
} = defineProps<Props>()
const emit = defineEmits<Emits>()

const { isFocused } = useProductCardMedia(() => focused)

const { $feature, $gtm } = useNuxtApp()
const origin = useOrigin()
const route = useRoute()
const router = useRouter()
const { getIsComingSoonProduct } = useComingSoon()

const PanelQuickshop = createPanel('quickshop')

const productUrl = ref(product.url)
const defaultColor = getDefaultColorOption(product.attributes, product.id) || {}
const swatchId = ref(defaultColor.id || product.id)

const showEyebrow = computed(() => {
  const { mode, routes } = $feature.eyebrowVisibilityOnRoute
  const match = routes.some((pattern) => convertPatternToRegex(pattern).test(route.path))
  return mode === 'show' ? match : !match
})

const productDetailsKey = computed(() => `productDetails-${swatchId.value}`)
const { data } = useApi().products.details(swatchId, {}, {
  immediate: false,
  key: productDetailsKey,
  default: () => ({
    ...product,
    url: defaultColor.url || productUrl.value,
    eyebrow: defaultColor.eyebrow || product.eyebrow,
    badge: defaultColor.badge || product.badge
  }),
  transform: (input) => ({
    ...input,
    eyebrow: formatBadgeOrEyebrow(
      input.eyebrow || product.eyebrow,
      product?.targetPublishDate,
      $feature.configProductComingSoonDateFormat
    ),
    badge: formatBadgeOrEyebrow(
      input.badge || product.badge,
      product?.targetPublishDate,
      $feature.configProductComingSoonDateFormat
    )
  })
})

const galleryContextKey = inject(GalleryContextKey)
const selectedColor = computed(() => {
  const options = product.attributes?.find(({ type }) => type.toLowerCase() === 'color')?.options as ColorOption[] | undefined
  return options?.find(({ id }) => id === swatchId.value)
})

const { activeGallery, galleryPending } = useActiveGallery(computed(() => ({
  id: swatchId.value,
  gallery: selectedColor.value?.gallery || product.gallery || data.value?.gallery,
  altGalleries: selectedColor.value?.altGalleries
    || (product as Product).altGalleries
    || (data.value as Product | undefined)?.altGalleries
})), galleryContextKey)

const categoryURL = breadcrumbs[breadcrumbs.length - 1]?.url
const colorsCount = 'availableColorsCount' in product ? product.availableColorsCount : 0
const url = computed(() => {
  if (data.value?.url && storeFilterState) {
    const baseUrl = `${data.value.url.startsWith('/') ? origin : ''}${data.value.url}`
    const parsedUrl = new URL(baseUrl)

    parsedUrl.searchParams.append('storeFilter', storeFilterState)

    return `${parsedUrl.pathname}${parsedUrl.search}`
  }

  return data.value?.url
})

const isComingSoon = computed(() => $feature.showComingSoonOnQuickshop
  && getIsComingSoonProduct(data.value.badge, data.value.eyebrow))
const isRobisOnly = computed(() => $feature.allowProductReservationOnPDP && data.value?.isRobisOnly)

const hasBadge = computed(() => {
  const { mode, routes } = $feature.badgeVisibilityOnRoute
  const match = routes.some((pattern) => convertPatternToRegex(pattern).test(route.path))
  const isVisible = mode === 'show' ? match : !match
  return !!data.value.badge && isVisible
})

const productPrice = computed(() => {
  if (!$feature.enablePlpSwatchDetails || data.value.displayPriceRange) return data.value.price

  const swatches = data.value?.attributes.find(({ type }) => type === 'color')?.options as ColorOption[]
  const colorSwatch = swatches?.find(({ id }) => id === swatchId.value) as ColorOption
  return colorSwatch?.price || data.value.price
})

const handleGtmEvent = (payload, action: ProductClickAction) =>
  $gtm.push('product.onProductClick', payload, {
    action,
    breadcrumbs,
    category: trackingCategory,
    list: trackingList,
    offset: trackingOffset,
    searchTerm: route.query.q?.toString()
  })

const navigateToPDP = (e?: PointerEvent) => {
  handleGtmEvent(data.value, 'Navigate To PDP')
  emit('product-click', product)

  const serializedBreadcrumbs = serializeAnalyticsBreadcrumbs(breadcrumbs)
  const state = {
    breadcrumbs: serializedBreadcrumbs,
    category: trackingCategory,
    list: trackingList,
    searchTerm: route.query.q,
    categoryURL,
    galleryContext: galleryContextKey?.value
  }

  if (e?.metaKey || e?.ctrlKey) {
    localStorage.setItem(`from_plp_${data.value.id}`, JSON.stringify(state))
  }
  else {
    e?.preventDefault()
    router.push({
      path: url.value.split('?')[0],
      query: Object.fromEntries(new URLSearchParams(url.value.split('?')[1])),
      state
    })
  }
}

const openQuickshop = () => {
  PanelQuickshop.open({
    product: data.value as Product,
    categoryURL,
    variant: 'detailed',
    gtmConfig: {
      ...(trackingCategory && { category: trackingCategory }),
      ...(trackingList && { list: trackingList }),
      ...(storeFilterState && { shippingMethod: 'BOPIS_PLP_SD', shippingStoreId: storeFilterState, shippingWindow: '24Hrs' })
    }
  })
  handleGtmEvent(data.value, 'Open Quick Shop')
}
</script>
